import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatMYR } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, CalendarIcon, ChevronDown, ChevronUp, Truck, ClipboardList, Search, UserRound } from "lucide-react";
import { newRid } from "../lib/rid";

const PLATFORMS = ["Shopee", "Lazada", "Tiktok", "WhatsApp", "Website"];
const PAYMENTS = ["Shopee", "Lazada", "Tiktok", "Debit Card/Credit Card", "Paypal", "Online Transfer"];
const DELIVERY_METHODS = ["自取", "邮寄", "送货", "面交"];
const ORDER_STATUS_OPTIONS = [
    { value: "completed", label: "已完成" },
    { value: "pending", label: "待处理 (Pending)" },
    { value: "to_collect", label: "待自取 (To be collect)" },
    { value: "booked", label: "已预订 (Booked)" },
];
const OTHER = "__other__";
const NEW_CUSTOMER = "__new__";

function CustomerSearchPicker({ customers, value, onChange }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");

    const selected = value !== NEW_CUSTOMER ? customers.find((c) => c.id === value) : null;
    const btnLabel = selected
        ? `${selected.name}${selected.phone ? " · " + selected.phone : ""}`
        : "➕ 新客户 / 散客";

    const filtered = q.trim()
        ? customers.filter((c) => {
            const t = q.trim().toLowerCase();
            return (c.name || "").toLowerCase().includes(t)
                || (c.phone || "").toLowerCase().includes(t);
        })
        : customers.slice(0, 30);

    const pick = (val) => {
        onChange(val);
        setOpen(false);
        setQ("");
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    data-testid="sale-customer-select"
                    className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-left text-sm flex items-center justify-between hover:border-gold-400 transition-colors"
                >
                    <span className="truncate">{btnLabel}</span>
                    <ChevronDown className="h-4 w-4 text-slate-400 shrink-0 ml-2" />
                </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-[340px] p-0 border-gold-200">
                <div className="p-2 border-b border-gold-100 bg-gold-50/40">
                    <div className="relative">
                        <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gold-500" />
                        <input
                            autoFocus
                            data-testid="sale-customer-search-input"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="输入姓名或电话..."
                            className="w-full h-9 pl-8 pr-3 text-sm rounded-md border border-slate-200 bg-white focus:outline-none focus:border-gold-500"
                        />
                    </div>
                </div>
                <div className="max-h-72 overflow-y-auto">
                    <button
                        type="button"
                        data-testid="sale-customer-opt-new"
                        onClick={() => pick(NEW_CUSTOMER)}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-gold-50 border-b border-slate-100 flex items-center gap-2 ${value === NEW_CUSTOMER ? "bg-gold-100 text-gold-800 font-medium" : "text-slate-700"}`}
                    >
                        <Plus className="h-3.5 w-3.5" /> 新客户 / 散客
                    </button>
                    {filtered.length === 0 ? (
                        <div className="px-3 py-6 text-center text-xs text-slate-500">
                            <UserRound className="h-6 w-6 mx-auto text-slate-300 mb-1" />
                            {customers.length === 0 ? "还没有客户" : "没有匹配的结果"}
                        </div>
                    ) : (
                        filtered.map((c) => (
                            <button
                                key={c.id}
                                type="button"
                                data-testid={`sale-customer-opt-${c.id}`}
                                onClick={() => pick(c.id)}
                                className={`w-full px-3 py-2 text-left text-sm hover:bg-gold-50 border-b border-slate-50 flex items-center justify-between gap-2 ${value === c.id ? "bg-gold-100 text-gold-800" : "text-slate-700"}`}
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="font-medium truncate">{c.name}</div>
                                    {c.phone && <div className="text-[11px] font-mono text-slate-500 truncate">{c.phone}</div>}
                                </div>
                                {c.tier && (
                                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 ${
                                        c.tier === "diamond" ? "bg-purple-100 text-purple-800"
                                        : c.tier === "gold" ? "bg-gold-200 text-gold-800"
                                        : "bg-slate-200 text-slate-700"
                                    }`}>
                                        {c.tier === "diamond" ? "钻卡" : c.tier === "gold" ? "金卡" : "银卡"}
                                    </span>
                                )}
                            </button>
                        ))
                    )}
                </div>
                {customers.length > 30 && !q.trim() && (
                    <div className="px-3 py-2 text-[10px] text-slate-500 border-t border-slate-100 bg-slate-50/60">
                        显示前 30 位 · 使用搜索找更多
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
}


export default function NewSalePage() {
    const [products, setProducts] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [items, setItems] = useState([{ _rid: newRid(), product_id: "", quantity: 1, unit_price: 0 }]);

    // Customer
    const [customerChoice, setCustomerChoice] = useState(NEW_CUSTOMER); // customer id or NEW_CUSTOMER
    const [customerName, setCustomerName] = useState("");
    const [customerPhone, setCustomerPhone] = useState("");

    const [discount, setDiscount] = useState(0);
    const [discountPercent, setDiscountPercent] = useState(0);

    const [platformChoice, setPlatformChoice] = useState("");
    const [platformOther, setPlatformOther] = useState("");
    const [paymentChoice, setPaymentChoice] = useState("");
    const [paymentOther, setPaymentOther] = useState("");
    const [saleDate, setSaleDate] = useState(new Date());
    const [dateOpen, setDateOpen] = useState(false);

    // Order lifecycle
    const [orderStatus, setOrderStatus] = useState("completed");

    // Delivery details
    const [showDelivery, setShowDelivery] = useState(false);
    const [deliveryMethod, setDeliveryMethod] = useState("");
    const [deliveryAddress, setDeliveryAddress] = useState("");
    const [deliveryContact, setDeliveryContact] = useState("");
    const [trackingNo, setTrackingNo] = useState("");

    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        api.get("/products").then((r) => setProducts(r.data));
        api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    }, []);

    const productMap = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);

    const onCustomerChoiceChange = (v) => {
        setCustomerChoice(v);
        if (v === NEW_CUSTOMER) {
            setCustomerName("");
            setCustomerPhone("");
        } else {
            const c = customers.find((x) => x.id === v);
            if (c) {
                setCustomerName(c.name || "");
                setCustomerPhone(c.phone || "");
                if (c.address && !deliveryAddress) setDeliveryAddress(c.address);
            }
        }
    };

    const updateItem = (idx, patch) => {
        const next = [...items];
        next[idx] = { ...next[idx], ...patch };
        if (patch.product_id) {
            const p = productMap[patch.product_id];
            if (p) next[idx].unit_price = p.unit_price;
        }
        setItems(next);
    };

    const addRow = () => setItems([...items, { _rid: newRid(), product_id: "", quantity: 1, unit_price: 0 }]);
    const removeRow = (idx) => setItems(items.filter((_, i) => i !== idx));

    const subtotal = items.reduce((s, it) => s + Number(it.quantity || 0) * Number(it.unit_price || 0), 0);
    const percentValue = Math.max(0, Math.min(100, Number(discountPercent || 0)));
    const percentDiscount = +(subtotal * percentValue / 100).toFixed(2);
    const amountDiscount = Math.max(0, Number(discount || 0));
    const effectiveDiscount = Math.min(subtotal, amountDiscount + percentDiscount);
    const total = Math.max(0, subtotal - effectiveDiscount);

    const resolvePlatform = () => (platformChoice === OTHER ? platformOther.trim() : platformChoice);
    const resolvePayment = () => (paymentChoice === OTHER ? paymentOther.trim() : paymentChoice);

    const submit = async (e) => {
        e.preventDefault();
        const valid = items.filter((it) => it.product_id && Number(it.quantity) > 0);
        if (valid.length === 0) {
            toast.error("至少添加一件商品");
            return;
        }
        setSaving(true);
        try {
            const isToday = saleDate && new Date().toDateString() === saleDate.toDateString();
            const iso = saleDate
                ? `${saleDate.getFullYear()}-${String(saleDate.getMonth() + 1).padStart(2, "0")}-${String(saleDate.getDate()).padStart(2, "0")}`
                : null;
            const payload = {
                customer_name: customerName,
                customer_phone: customerPhone,
                customer_id: customerChoice !== NEW_CUSTOMER ? customerChoice : null,
                items: valid.map((it) => ({
                    product_id: it.product_id,
                    quantity: Number(it.quantity),
                    unit_price: Number(it.unit_price),
                })),
                discount: amountDiscount,
                discount_percent: percentValue,
                platform: resolvePlatform(),
                payment_method: resolvePayment(),
                note,
                order_status: orderStatus,
                delivery_method: deliveryMethod,
                delivery_address: deliveryAddress,
                delivery_contact: deliveryContact,
                tracking_no: trackingNo,
                ...(isToday ? {} : { sale_date: iso }),
            };
            const { data } = await api.post("/sales", payload);
            toast.success(`销售单已创建：${data.invoice_no}`);
            navigate("/sales");
        } catch (err) {
            toast.error(err.response?.data?.detail || "创建失败");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="new-sale-page">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-gold-500 hover:text-slate-800 transition-colors">
                <ArrowLeft className="h-4 w-4" /> 返回
            </button>
            <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-gold-500">新增</div>
                <h1 className="text-3xl font-bold tracking-tight text-slate-900 mt-1">新建销售单</h1>
                <p className="text-sm text-gold-500 mt-1">选择产品并填写数量，系统会自动扣减库存</p>
            </div>

            <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <Card className="p-6 border-slate-200 shadow-sm">
                        {/* Customer selector */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <div className="space-y-1.5 md:col-span-1">
                                <Label>选择客户（搜索姓名 / 电话）</Label>
                                <CustomerSearchPicker
                                    customers={customers}
                                    value={customerChoice}
                                    onChange={onCustomerChoiceChange}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label>客户姓名</Label>
                                <Input
                                    data-testid="sale-customer-input"
                                    value={customerName}
                                    onChange={(e) => setCustomerName(e.target.value)}
                                    placeholder="散客"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label>联系电话</Label>
                                <Input
                                    data-testid="sale-customer-phone-input"
                                    value={customerPhone}
                                    onChange={(e) => setCustomerPhone(e.target.value)}
                                    placeholder="+60 12-345 6789"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                            <div className="space-y-1.5">
                                <Label>开单日期</Label>
                                <Popover open={dateOpen} onOpenChange={setDateOpen}>
                                    <PopoverTrigger asChild>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            data-testid="sale-date-btn"
                                            className="w-full h-10 justify-start gap-2 font-mono text-sm"
                                        >
                                            <CalendarIcon className="h-4 w-4 text-gold-500" />
                                            {saleDate
                                                ? `${saleDate.getFullYear()}-${String(saleDate.getMonth() + 1).padStart(2, "0")}-${String(saleDate.getDate()).padStart(2, "0")}`
                                                : "选择日期"}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent align="start" className="p-0 w-auto">
                                        <div className="p-2 border-b border-slate-100 flex gap-1">
                                            <button type="button" onClick={() => { setSaleDate(new Date()); setDateOpen(false); }} className="text-xs px-3 py-1.5 rounded hover:bg-slate-100 text-slate-700">今天</button>
                                            <button type="button" onClick={() => { const d = new Date(); d.setDate(d.getDate() - 1); setSaleDate(d); setDateOpen(false); }} className="text-xs px-3 py-1.5 rounded hover:bg-slate-100 text-slate-700">昨天</button>
                                        </div>
                                        <Calendar
                                            mode="single"
                                            selected={saleDate}
                                            onSelect={(d) => { if (d) { setSaleDate(d); setDateOpen(false); } }}
                                            disabled={(d) => d > new Date()}
                                            initialFocus
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>
                            <div className="space-y-1.5">
                                <Label>销售平台</Label>
                                <Select value={platformChoice} onValueChange={setPlatformChoice}>
                                    <SelectTrigger data-testid="sale-platform-select">
                                        <SelectValue placeholder="选择平台" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {PLATFORMS.map((p) => (
                                            <SelectItem key={p} value={p}>{p}</SelectItem>
                                        ))}
                                        <SelectItem value={OTHER}>其他 (自定义)</SelectItem>
                                    </SelectContent>
                                </Select>
                                {platformChoice === OTHER && (
                                    <Input
                                        data-testid="sale-platform-other-input"
                                        value={platformOther}
                                        onChange={(e) => setPlatformOther(e.target.value)}
                                        placeholder="请输入平台名称"
                                        className="mt-1"
                                    />
                                )}
                            </div>
                            <div className="space-y-1.5">
                                <Label>付款方式</Label>
                                <Select value={paymentChoice} onValueChange={setPaymentChoice}>
                                    <SelectTrigger data-testid="sale-payment-select">
                                        <SelectValue placeholder="选择付款方式" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {PAYMENTS.map((p) => (
                                            <SelectItem key={p} value={p}>{p}</SelectItem>
                                        ))}
                                        <SelectItem value={OTHER}>其他 (自定义)</SelectItem>
                                    </SelectContent>
                                </Select>
                                {paymentChoice === OTHER && (
                                    <Input
                                        data-testid="sale-payment-other-input"
                                        value={paymentOther}
                                        onChange={(e) => setPaymentOther(e.target.value)}
                                        placeholder="请输入付款方式"
                                        className="mt-1"
                                    />
                                )}
                            </div>
                            <div className="space-y-1.5">
                                <Label className="inline-flex items-center gap-1.5"><ClipboardList className="h-3.5 w-3.5 text-gold-500" /> 订单状态</Label>
                                <Select value={orderStatus} onValueChange={setOrderStatus}>
                                    <SelectTrigger data-testid="sale-status-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {ORDER_STATUS_OPTIONS.map((o) => (
                                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="border-t border-slate-200 -mx-6 px-6 pt-4">
                            <div className="flex items-center justify-between mb-3">
                                <h2 className="text-sm font-semibold uppercase tracking-wider text-gold-500">商品明细</h2>
                                <Button type="button" size="sm" variant="outline" onClick={addRow} data-testid="add-line-btn" className="gap-1.5">
                                    <Plus className="h-3.5 w-3.5" /> 添加行
                                </Button>
                            </div>

                            <div className="space-y-3">
                                {items.map((it, idx) => {
                                    const p = productMap[it.product_id];
                                    const stockWarn = p && Number(it.quantity) > p.stock;
                                    return (
                                        <div key={it._rid} className="grid grid-cols-12 gap-2 items-start border border-slate-200 rounded-md p-3">
                                            <div className="col-span-5 space-y-1">
                                                <Label className="text-xs text-gold-500">产品</Label>
                                                <Select value={it.product_id} onValueChange={(v) => updateItem(idx, { product_id: v })}>
                                                    <SelectTrigger data-testid={`line-product-${idx}`}>
                                                        <SelectValue placeholder="选择产品" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {products.map((pr) => (
                                                            <SelectItem key={pr.id} value={pr.id}>
                                                                {pr.name} · {pr.sku} (库存 {pr.stock})
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="col-span-2 space-y-1">
                                                <Label className="text-xs text-gold-500">数量</Label>
                                                <Input
                                                    data-testid={`line-qty-${idx}`}
                                                    type="number"
                                                    min="1"
                                                    value={it.quantity}
                                                    onChange={(e) => updateItem(idx, { quantity: e.target.value })}
                                                    className={`font-mono ${stockWarn ? "border-red-400" : ""}`}
                                                />
                                            </div>
                                            <div className="col-span-2 space-y-1">
                                                <Label className="text-xs text-gold-500">单价 (RM)</Label>
                                                <Input
                                                    data-testid={`line-price-${idx}`}
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    value={it.unit_price}
                                                    onChange={(e) => updateItem(idx, { unit_price: e.target.value })}
                                                    className="font-mono"
                                                />
                                            </div>
                                            <div className="col-span-2 space-y-1">
                                                <Label className="text-xs text-gold-500">小计</Label>
                                                <div className="h-10 flex items-center px-3 rounded-md bg-gold-50 text-sm font-mono font-semibold text-slate-900">
                                                    {formatMYR(Number(it.quantity || 0) * Number(it.unit_price || 0))}
                                                </div>
                                            </div>
                                            <div className="col-span-1 flex items-end h-full pb-0.5 justify-end">
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => removeRow(idx)}
                                                    disabled={items.length === 1}
                                                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                            {stockWarn && (
                                                <div className="col-span-12 text-xs text-red-600">
                                                    库存不足：{p.name} 当前库存 {p.stock}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </Card>

                    {/* Delivery details collapsible */}
                    <Card className="p-6 border-slate-200 shadow-sm">
                        <button
                            type="button"
                            data-testid="toggle-delivery-btn"
                            onClick={() => setShowDelivery((s) => !s)}
                            className="w-full flex items-center justify-between text-left"
                        >
                            <div className="flex items-center gap-2">
                                <Truck className="h-4 w-4 text-gold-500" />
                                <span className="text-sm font-semibold text-slate-900">配送信息 (可选)</span>
                                {(deliveryMethod || deliveryAddress || trackingNo) && (
                                    <span className="text-xs bg-gold-100 text-gold-800 rounded-full px-2 py-0.5 font-medium">已填写</span>
                                )}
                            </div>
                            {showDelivery ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                        </button>
                        {showDelivery && (
                            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <Label>配送方式</Label>
                                    <Select value={deliveryMethod} onValueChange={setDeliveryMethod}>
                                        <SelectTrigger data-testid="delivery-method-select">
                                            <SelectValue placeholder="选择配送方式" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {DELIVERY_METHODS.map((m) => (
                                                <SelectItem key={m} value={m}>{m}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-1.5">
                                    <Label>快递单号 / Tracking</Label>
                                    <Input data-testid="delivery-tracking-input" value={trackingNo} onChange={(e) => setTrackingNo(e.target.value)} placeholder="例如：EE123456789MY" />
                                </div>
                                <div className="space-y-1.5 md:col-span-2">
                                    <Label>收货地址</Label>
                                    <Textarea rows={2} data-testid="delivery-address-input" value={deliveryAddress} onChange={(e) => setDeliveryAddress(e.target.value)} placeholder="邮递或送货地址" />
                                </div>
                                <div className="space-y-1.5 md:col-span-2">
                                    <Label>收件人联系方式</Label>
                                    <Input data-testid="delivery-contact-input" value={deliveryContact} onChange={(e) => setDeliveryContact(e.target.value)} placeholder="收件人姓名 / 电话" />
                                </div>
                            </div>
                        )}
                    </Card>

                    <Card className="p-6 border-slate-200 shadow-sm">
                        <div className="space-y-1.5">
                            <Label>备注</Label>
                            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="可留空" />
                        </div>
                    </Card>
                </div>

                <div className="space-y-4">
                    <Card className="p-6 border-slate-200 shadow-sm sticky top-6">
                        <h2 className="text-lg font-semibold tracking-tight text-slate-900 mb-4">结算</h2>
                        <div className="space-y-3">
                            <div className="flex justify-between text-sm">
                                <span className="text-gold-500">商品小计</span>
                                <span className="font-mono font-medium text-slate-900">{formatMYR(subtotal)}</span>
                            </div>

                            <div className="flex justify-between items-center text-sm gap-2">
                                <span className="text-gold-500 shrink-0">折扣百分比</span>
                                <div className="flex items-center gap-1.5">
                                    <Input
                                        data-testid="sale-discount-percent-input"
                                        type="number"
                                        step="0.1"
                                        min="0"
                                        max="100"
                                        value={discountPercent}
                                        onChange={(e) => setDiscountPercent(e.target.value)}
                                        className="w-20 h-9 text-right font-mono"
                                    />
                                    <span className="text-sm text-gold-500">%</span>
                                </div>
                            </div>
                            {percentDiscount > 0 && (
                                <div className="flex justify-between text-xs text-gold-500 -mt-2 pl-3">
                                    <span>= 按百分比折扣</span>
                                    <span className="font-mono text-red-600">- {formatMYR(percentDiscount)}</span>
                                </div>
                            )}

                            <div className="flex justify-between items-center text-sm">
                                <span className="text-gold-500">折扣金额 (RM)</span>
                                <Input
                                    data-testid="sale-discount-input"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={discount}
                                    onChange={(e) => setDiscount(e.target.value)}
                                    className="w-28 h-9 text-right font-mono"
                                />
                            </div>

                            <div className="border-t border-slate-200 pt-3 space-y-2">
                                <div className="flex justify-between text-xs text-gold-500">
                                    <span>合计折扣</span>
                                    <span className="font-mono">- {formatMYR(effectiveDiscount)}</span>
                                </div>
                                <div className="flex justify-between items-baseline">
                                    <span className="text-sm font-semibold uppercase tracking-wider text-gold-500">合计</span>
                                    <span className="text-2xl font-bold font-mono text-gold-600">{formatMYR(total)}</span>
                                </div>
                            </div>
                        </div>
                        <Button type="submit" disabled={saving} data-testid="submit-sale-btn" className="w-full mt-6 h-11 bg-gold-600 hover:bg-gold-700 text-white font-semibold">
                            {saving ? "创建中..." : "确认并生成发票"}
                        </Button>
                    </Card>
                </div>
            </form>
        </div>
    );
}
